import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { Table } from "antd";
import FloatOut from "./float-out";
import ExportButton from "./export-button";
import TableColumnsSelector from "./table-columns-selector";
import { loadSelectedTableColumns, saveSelectedTableColumns } from "../../helpers/local-storage";

// get string identifier ("key") of column
// note that dataIndex can be an array of strings instead of a single string
const dataIndexToString = dataIndex => (Array.isArray(dataIndex) ? dataIndex.join(".") : dataIndex);

const TableWithControls = ({
  // data fetching
  queries,
  fetcher,
  onChange,
  // table
  columns,
  columnKeysSelected,
  columnKeyGetter = col => col.key || dataIndexToString(col.dataIndex),
  rowKey,
  tableKey,
  size = "middle",
  expandable,
  // columns selector
  shouldSelectColumns = true,
  // export
  shouldExport = true,
  exportESUrl,
  exportESQueryBuilder = q => q,
  exportQuota = 100000,
  // additional float-out
  customFloatOut,
  // pagination
  shouldPaginate = true,
}) => {
  // data fetching
  const [data, setData] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  // const [error, setError] = useState(false);
  // table columns
  const [colsSelected, setColsSelected] = useState(columnKeysSelected);
  // table pagination
  const [pageSize, setPageSize] = useState(20);
  const [pageCurrent, setPageCurrent] = useState(1);

  const quotaExceeded = total > exportQuota;

  useEffect(() => {
    let isMounted = true; // add this line
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await fetcher(queries);
        if (isMounted) { // check if component is still mounted
          setData(response.data);
          setTotal(response.total);
          setLoading(false);
          // setError(false);
        }
      } catch (e) {
        if (isMounted) { // check if component is still mounted
          setLoading(false);
          // setError(true);
        }
      }
    };
    fetchData();
    return () => { isMounted = false }; // clean up function to set isMounted to false when component unmounts
  }, [queries, fetcher]);

  useEffect(() => {
    let isMounted = true; // add this line
    if (shouldSelectColumns) {
      const savedColIndices = loadSelectedTableColumns(`${tableKey}Columns`);
      if (isMounted && savedColIndices) { // check if component is still mounted
        setColsSelected(savedColIndices);
      }
    }
    return () => { isMounted = false }; // clean up function to set isMounted to false when component unmounts
  }, [tableKey, shouldSelectColumns]);

  const getSortOrder = columnKey => queries.sort && queries.sort.indexOf(columnKey) > -1
    ? queries.sort.substring(0, 1) === "-"
      ? "descend"
      : "ascend"
    : false;
  const columnsWithKeys = columns
    .map(col => ({
      ...col,
      key: columnKeyGetter(col),
      sortOrder: col.sorter ? getSortOrder(columnKeyGetter(col)) : undefined,
    }))
    .sort((small, big) => {
      // match the order of columns in colsSelected while unselected ones keeping the original order of columns
      const smallIndex = colsSelected.indexOf(small.key);
      const bigIndex = colsSelected.indexOf(big.key);
      return smallIndex >= 0 && bigIndex >= 0 ? smallIndex - bigIndex : smallIndex >= 0 ? -1 : bigIndex >= 0 ? 1 : 0;
    });
  const tableHeaders = columnsWithKeys.filter(col => colsSelected.indexOf(col.key) >= 0);
  const tableScroll = { x: tableHeaders.reduce((acc, col) => acc + (col.width || 80), 0) };

  const pagination = shouldPaginate && {
    total,
    showTotal: total => `${total} Found`,
    defaultPageSize: pageSize,
    pageSize,
    current: pageCurrent,
    itemRender: (page, type, item) => {
        // ES result window is currently set to 1,000,000 results. Past this, it will fail to return anything.
        // Originally, the last number of the pagination was hidden to avoid this issue but it affected all pagination sizes.
        // This only hides the last page number for large paginations of 1000+ pages.
        // This does not solve the ES result window issue but it is unrealistic that a user would ever need to go that far.
        const totalPages = Math.ceil(total / pageSize);
        if (type === "page" && page === totalPages && totalPages > 1000) {
            return null;
        }
        return item;
    }
  };

  const onTableChange = (pagination, filters, sorters) => {
    setPageCurrent(pagination.current);
    setPageSize(pagination.pageSize);
    onChange({
      ...queries,
      limit: pagination.pageSize,
      offset: (pagination.current - 1) * pagination.pageSize,
      sort: sorters.column ? (sorters.order === "descend" ? "-" : "") + columnKeyGetter(sorters.column) : undefined,
    });
  };

  const onColumnKeysSelectedChange = indicesNew => {
    setColsSelected(indicesNew);
    saveSelectedTableColumns(`${tableKey}Columns`, indicesNew);
  }

  // TODO: handle specific auth roles like `disallow-*-export`
  const key = `${tableKey}_${JSON.stringify(queries)}_${colsSelected.join("_")}`
  return (
    <>
      <FloatOut style={{ display: "flex", gap: 8 }}>
        {customFloatOut}
        {shouldSelectColumns ? (
          <TableColumnsSelector
            key={`select__${key}`}
            columns={columnsWithKeys}
            onChange={onColumnKeysSelectedChange}
            columnKeysSelected={colsSelected}
          />
        ) : null}
        {shouldExport ? (
          <ExportButton
            key={`export__${key}`}
            url={exportESUrl}
            queries={exportESQueryBuilder(queries)}
            columns={columnsWithKeys}
            columnKeysSelected={colsSelected}
            quotaExceeded={quotaExceeded}
            quota={exportQuota}
          />
        ) : null}
      </FloatOut>
      <Table
        key={key}
        columns={tableHeaders}
        dataSource={data}
        rowKey={rowKey}
        loading={loading}
        onChange={onTableChange}
        pagination={pagination}
        expandable={expandable}
        scroll={tableScroll}
        size={size}
      />
    </>
  )
};

TableWithControls.propTypes = {
  queries: PropTypes.object.isRequired,
  fetcher: PropTypes.func.isRequired,
  onChange: PropTypes.func.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    title: PropTypes.oneOfType([PropTypes.string, PropTypes.element]).isRequired,
    dataIndex: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
    width: PropTypes.number.isRequired,
    render: PropTypes.func,
    sorter: PropTypes.bool,
    key: PropTypes.string,
  })).isRequired,
  columnKeysSelected: PropTypes.array.isRequired,
  columnKeyGetter: PropTypes.func,
  rowKey: PropTypes.string.isRequired,
  tableKey: PropTypes.string.isRequired,
  expandable: PropTypes.shape({
    expandedRowRender: PropTypes.func.isRequired,
    rowExpandable: PropTypes.func,
  }),
  size: PropTypes.oneOf(["small", "middle", "large"]),
  shouldSelectColumns: PropTypes.bool,
  shouldExport: PropTypes.bool,
  exportESUrl: PropTypes.string,
  exportESQueryBuilder: PropTypes.func,
  exportQuota: PropTypes.number,
  customFloatOut: PropTypes.element,
  shouldPaginate: PropTypes.bool,
};

export default TableWithControls;